import React, { useContext, useEffect, useState } from 'react'
import { useParams, useNavigate, NavLink } from 'react-router-dom'
import axios from 'axios'
import Swal from 'sweetalert2'
import PageTitle from '../../../Components/PageTitle'
import { ConfigContext } from '../../../Context/ConfigContext'
import { SubmitBtn } from '../../../Components/InputElements'
import { ContentLoader, NoRecords, TableRows } from '../../../Components/Shimmer'

const ProductFeatures = () => {
    const { primaryColor, apiHeaderJson, apiURL } = useContext(ConfigContext)
    const { product_id } = useParams()
    const navigate = useNavigate()

    // State for the list of features
    const [features, setFeatures] = useState([])
    const [loading, setLoading] = useState(true)
    const [submitLoading, setSubmitLoading] = useState(false)
    const [isInfoLoading, setIsInfoLoading] = useState(false)

    // Form state
    const [featureText, setFeatureText] = useState('')
    const [featureSeq, setFeatureSeq] = useState('') // NEW: sequence
    const [featureId, setFeatureId] = useState(null) // null means new, else updating
    const [errors, setErrors] = useState({})

    // Fetch all features for this product
    const fetchFeatures = async () => {
        try {
            setLoading(true)
            const response = await axios.get(`${apiURL}Masters/GetProductFeatures`, {
                headers: apiHeaderJson,
                params: { product_id }
            })
            const { success, data, total_records } = response.data
            if (success) {
                setFeatures(data || [])
                setFeatureSeq(total_records + 1)
            } else {
                Swal.fire('Error!', response.message || 'Failed to load features', 'error')
            }
        } catch (error) {
            console.error(error)
            Swal.fire('Error!', 'Something went wrong while fetching features', 'error')
        } finally {
            setLoading(false)
        }
    }

    // Fetch single feature info for editing
    const fetchFeatureInfo = async (feature_id) => {
        try {
            setIsInfoLoading(true)
            const response = await axios.get(`${apiURL}Masters/GetProductFeatureInfo`, {
                headers: apiHeaderJson,
                params: { feature_id }
            })
            const { success, data } = response.data
            if (success && data && data.length > 0) {
                const feature = data[0]
                setFeatureText(feature.feature || '')
                setFeatureSeq(feature.feature_seq ?? '') // NEW: set sequence
                setFeatureId(feature.feature_id)
                setErrors({})
            } else {
                Swal.fire('Error!', response.message || 'Failed to load feature info', 'error')
            }
        } catch (error) {
            console.error(error)
            Swal.fire('Error!', 'Something went wrong', 'error')
        } finally {
            setIsInfoLoading(false)
        }
    }

    // Reset form to add new feature
    const resetForm = () => {
        setFeatureText('')
        setFeatureSeq('') // NEW: reset sequence
        setFeatureId(null)
        setErrors({})
    }

    // Validate form
    const validateForm = () => {
        const errors = {}
        if (!featureText.trim()) {
            errors.feature = 'Feature text is required'
        }
        // Optional: validate sequence is a number if provided
        if (featureSeq && isNaN(featureSeq)) {
            errors.feature_seq = 'Sequence must be a number'
        }
        setErrors(errors)
        return Object.keys(errors).length === 0
    }

    // Handle submit (create or update)
    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validateForm()) return

        setSubmitLoading(true)
        try {
            const payload = {
                product_id: product_id,
                feature: featureText.trim(),
                feature_seq: featureSeq ? Number(featureSeq) : null // NEW: include sequence
            }
            if (featureId) {
                payload.feature_id = featureId
            }

            const response = await axios.post(`${apiURL}Masters/CreateProductFeature`, payload, {
                headers: apiHeaderJson
            })
            const { message, success } = response.data

            if (success) {
                Swal.fire('Success!', message, 'success')
                resetForm()
                fetchFeatures() // refresh the list
            } else {
                Swal.fire('Error!', message || 'Failed to save feature', 'error')
            }
        } catch (error) {
            console.error(error)
            Swal.fire('Error!', 'Something went wrong', 'error')
        } finally {
            setSubmitLoading(false)
        }
    }

    // Handle delete
    const handleDelete = async (feature_id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        })
        if (result.isConfirmed) {
            try {
                setLoading(true) // show loader while deleting
                const response = await axios.post(`${apiURL}Masters/DeleteProductFeature?feature_id=${feature_id}`,
                    { feature_id },
                    { headers: apiHeaderJson }
                )
                const { message, success } = response.data
                Swal.fire('Deleted!', message, success ? 'success' : 'error')
                if (success) {
                    fetchFeatures() // refresh list
                }
            } catch (error) {
                console.error(error)
                Swal.fire('Error!', 'Something went wrong', 'error')
            } finally {
                setLoading(false)
            }
        }
    }

    // Load features on mount and when product_id changes
    useEffect(() => {
        if (product_id) {
            fetchFeatures()
        }
    }, [product_id])

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <PageTitle title="Manage Product Features" primary="Products" />
                    {isInfoLoading && <ContentLoader />}

                    <div className="row">
                        {/* Left column: Form */}
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-header" style={{ backgroundColor: primaryColor }}>
                                    <h4 className="card-title mb-0 text-white">
                                        {featureId ? 'Update Feature' : 'Add New Feature'}
                                    </h4>
                                </div>
                                <div className="card-body">
                                    <form onSubmit={handleSubmit}>
                                        <div className="mb-3">
                                            <label htmlFor="featureText" className="form-label">
                                                Feature <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                id="featureText"
                                                className={`form-control ${errors.feature ? 'is-invalid' : ''}`}
                                                placeholder="Enter feature"
                                                value={featureText}
                                                onChange={(e) => {
                                                    setFeatureText(e.target.value)
                                                    if (errors.feature) {
                                                        setErrors({ ...errors, feature: '' })
                                                    }
                                                }}
                                            />
                                            {errors.feature && (
                                                <div className="invalid-feedback">{errors.feature}</div>
                                            )}
                                        </div>

                                        {/* NEW: Sequence input */}
                                        <div className="mb-3">
                                            <label htmlFor="featureSeq" className="form-label">
                                                Sequence
                                            </label>
                                            <input
                                                id="featureSeq"
                                                type="number"
                                                className={`form-control ${errors.feature_seq ? 'is-invalid' : ''}`}
                                                placeholder="Enter sequence (optional)"
                                                value={featureSeq}
                                                onChange={(e) => {
                                                    setFeatureSeq(e.target.value)
                                                    if (errors.feature_seq) {
                                                        setErrors({ ...errors, feature_seq: '' })
                                                    }
                                                }}
                                            />
                                            {errors.feature_seq && (
                                                <div className="invalid-feedback">{errors.feature_seq}</div>
                                            )}
                                        </div>

                                        <div className="d-flex gap-2 justify-content-end">
                                            {featureId && (
                                                <button
                                                    type="button"
                                                    className="btn btn-light"
                                                    onClick={resetForm}
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                            <SubmitBtn
                                                disabled={submitLoading}
                                                text={submitLoading ? 'Saving...' : featureId ? 'Update' : 'Save'}
                                                type="primary"
                                                onClick={handleSubmit}
                                                icon="ri-save-line"
                                            />
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>

                        {/* Right column: List */}
                        <div className="col-md-8">
                            <div className="card">
                                <div className="card-header" style={{ backgroundColor: primaryColor }}>
                                    <h4 className="card-title mb-0 text-white">
                                        Features List
                                    </h4>
                                </div>
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-striped table-hover mb-0">
                                            {loading ? (
                                                <TableRows rows="5" colspan="4" />
                                            ) : (
                                                <>
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Seq</th> {/* NEW: Sequence column */}
                                                            <th>Feature</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {features.length > 0 ? (
                                                            features.map((row, index) => (
                                                                <tr key={row.feature_id}>
                                                                    <td>{index + 1}</td>
                                                                    <td>{row.feature_seq ?? '-'}</td> {/* NEW: show sequence */}
                                                                    <td>{row.feature}</td>
                                                                    <td>
                                                                        <button
                                                                            className="btn btn-sm btn-soft-primary me-2"
                                                                            onClick={() => fetchFeatureInfo(row.feature_id)}
                                                                            title="Edit"
                                                                        >
                                                                            <i className="ri-pencil-line" />
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-soft-danger"
                                                                            onClick={() => handleDelete(row.feature_id)}
                                                                            title="Delete"
                                                                        >
                                                                            <i className="ri-delete-bin-line" />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="4" className="text-center"> {/* changed colSpan to 4 */}
                                                                    <NoRecords />
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </>
                                            )}
                                        </table>
                                    </div>
                                    {/* Optional: Back to Product List */}
                                    <div className="mt-3">
                                        <NavLink
                                            to="/Masters/ProductList"
                                            className="btn btn-sm btn-secondary"
                                        >
                                            <i className="ri-arrow-left-line me-1" /> Back to Products
                                        </NavLink>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ProductFeatures